/**
 * Turn a customer's unbilled trips into an invoice.
 *
 * The clerk chooses what goes on it. That was an explicit requirement,
 * and it is the reason this is a selection screen rather than a "bill
 * the month" button: a disputed trip has to be held back without
 * delaying the other thirty.
 *
 * Everything is selected by default, because the common case is "all of
 * them" and a clerk should not tick thirty boxes to get there. Unticking
 * two is the exception, and the exception is what costs the clicks.
 *
 * The total updates from the server's own preview, not from arithmetic
 * done here. The server's figure is what gets posted, so a figure
 * computed in the browser could show one number and bill another.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Stack, TextField, Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  cancelTripInvoice, createTripInvoice, downloadTripInvoicePdf,
  fetchCapabilities, fetchTripInvoices, fetchTrips, previewInvoice,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type Trip, type TripInvoice } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { c, fmtMoney } from '../theme'
import SectionHead from '../components/SectionHead'
import LedgerPicker from '../vouchers/LedgerPicker'
import CreatePartyDialog from '../vouchers/CreatePartyDialog'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import Keycap from '../components/Keycap'
import { monthStart, today } from '../dates'
import CorrectInvoiceDialog, { NoteRow } from './CorrectInvoiceDialog'
import ScreenTitle from '../components/ScreenTitle'



export default function TripInvoicing() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const qc = useQueryClient()

  const [customer, setCustomer] = useState('')
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(today())
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<TripInvoice | null>(null)
  const [cancelling, setCancelling] = useState<TripInvoice | null>(null)
  const [reason, setReason] = useState('')
  const [newCustomer, setNewCustomer] = useState(false)
  const [correcting, setCorrecting] = useState<TripInvoice | null>(null)
  // The list of invoices has its own period and search: the period above
  // is for picking trips, and would hide last month's invoices.
  const [listFrom, setListFrom] = useState('')
  const [listTo, setListTo] = useState('')
  const [search, setSearch] = useState('')

  const mayInvoice = can(P.transportInvoice) && can(P.postSales)
  const caps = useQuery({ queryKey: ['capabilities'],
    queryFn: fetchCapabilities })

  const trips = useQuery({
    enabled: !!customer,
    queryKey: ['trips', 'unbilled', customer, from, to],
    queryFn: () => fetchTrips({ customer_code: customer, from_date: from,
                                to_date: to, unbilled_only: true }),
  })

  const invoices = useQuery({
    queryKey: ['trip-invoices', customer, listFrom, listTo],
    queryFn: () => fetchTripInvoices({
      ...(customer ? { customer_code: customer } : {}),
      ...(listFrom ? { from_date: listFrom } : {}),
      ...(listTo ? { to_date: listTo } : {}) }),
  })

  // Everything on by default -- "all of them" is the common case.
  useEffect(() => {
    setPicked(new Set((trips.data?.trips ?? []).map((x) => x.id)))
  }, [trips.data])

  const ids = useMemo(() => [...picked], [picked])

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    const all = invoices.data?.invoices ?? []
    return q ? all.filter((i) => i.invoice_no.toLowerCase().includes(q)
      || (i.customer_name ?? '').toLowerCase().includes(q)) : all
  }, [invoices.data, search])
  const filtered = !!(search.trim() || listFrom || listTo)

  const preview = useQuery({
    enabled: !!customer && ids.length > 0,
    queryKey: ['invoice-preview', customer, ids.join(',')],
    queryFn: () => previewInvoice({ customer_code: customer, trip_ids: ids }),
  })

  const toggle = useCallback((id: number) => {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const all = trips.data?.trips ?? []
  const allPicked = all.length > 0 && picked.size === all.length

  // Same key as everywhere else. A customer who has not been set up yet
  // should not send the clerk to another screen and back.
  useHotkeys('trip-invoicing', [
    { combo: 'alt+c', label: t('quick.newCustomer'), allowInInput: true,
      handler: () => setNewCustomer(true) },
  ])

  const create = useMutation({
    mutationFn: () => createTripInvoice({
      customer_code: customer, trip_ids: ids,
      invoice_date: today(), from_date: from, to_date: to }),
    onSuccess: (inv) => {
      setError(null)
      setDone(inv)
      void qc.invalidateQueries({ queryKey: ['trips'] })
      void qc.invalidateQueries({ queryKey: ['trip-invoices'] })
      void qc.invalidateQueries({ queryKey: ['outstanding'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const cancel = useMutation({
    mutationFn: () => cancelTripInvoice(cancelling!.id, reason),
    onSuccess: () => {
      setCancelling(null); setReason(''); setError(null)
      void qc.invalidateQueries({ queryKey: ['trips'] })
      void qc.invalidateQueries({ queryKey: ['trip-invoices'] })
      void qc.invalidateQueries({ queryKey: ['outstanding'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <Stack spacing={2}>
      <Paper variant="outlined">
        <ScreenTitle titleKey="nav.tripInvoicing" sub={t('invoice.sub')} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
          sx={{ px: 2.5, pt: 1, pb: 2, alignItems: { sm: 'flex-end' } }}>
          <Box sx={{ position: 'relative', minWidth: 240, flex: 1 }}>
            <Typography sx={{ fontSize: 11, color: c.inkMuted, mb: 0.5 }}>
              {t('trip.customer')}
            </Typography>
            <LedgerPicker value={customer}
              onChange={(code) => setCustomer(code)}
              groups={['DEBTORS']}
              placeholder={t('trip.customerPlaceholder')} />
            <Stack direction="row" spacing={0.5} alignItems="center"
              sx={{ mt: 0.5 }}>
              <Keycap combo="alt+c" />
              <Typography sx={{ fontSize: 11.5, color: c.inkMuted }}>
                {t('quick.newCustomer')}
              </Typography>
            </Stack>
          </Box>
          <TextField label={t('report.from')} type="date" size="small"
            value={from} onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
          <TextField label={t('report.to')} type="date" size="small"
            value={to} onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
        </Stack>

        {error && <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{error}</Alert>}

        {!customer && (
          <Typography variant="body2" sx={{ px: 2, pb: 3, color: c.inkMuted }}>
            {t('invoice.pickCustomer')}
          </Typography>
        )}

        {customer && all.length === 0 && !trips.isLoading && (
          <Typography variant="body2" sx={{ px: 2, pb: 3, color: c.inkMuted }}>
            {t('invoice.nothingUnbilled')}
          </Typography>
        )}

        {customer && all.length > 0 && (
          <Box sx={{ borderTop: `1px solid ${c.border}` }}>
            <Box component="table" sx={{ width: '100%',
                                         borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr" sx={{ bgcolor: c.surfacePage }}>
                  <Box component="th" sx={{ width: 44, p: 0 }}>
                    <Checkbox size="small" checked={allPicked}
                      indeterminate={picked.size > 0 && !allPicked}
                      onChange={() => setPicked(allPicked
                        ? new Set()
                        : new Set(all.map((x) => x.id)))}
                      inputProps={{ 'aria-label': t('invoice.selectAll') }} />
                  </Box>
                  {[t('trip.tripNo'), t('entry.date'), t('trip.vehicle'),
                    t('trip.fromTo'), t('trip.doNumber')].map((h) => (
                    <Box component="th" key={h}
                      sx={{ textAlign: 'start', px: 1.5, py: 1 }}>
                      <span className="section-label">{h}</span>
                    </Box>
                  ))}
                  <Box component="th" sx={{ textAlign: 'end', px: 1.5 }}>
                    <span className="section-label">{t('trip.rate')}</span>
                  </Box>
                </Box>
              </Box>
              <Box component="tbody">
                {all.map((trip: Trip) => (
                  <Box component="tr" key={trip.id}
                    onClick={() => toggle(trip.id)}
                    sx={{ cursor: 'pointer',
                          borderTop: `1px solid ${c.divider}`,
                          bgcolor: picked.has(trip.id) ? c.primarySoft : undefined,
                          '&:hover': { bgcolor: c.divider } }}>
                    {/* stopPropagation, or the click toggles twice:
                        once on the checkbox and once on the row beneath
                        it, which cancels out and looks like a dead
                        control. */}
                    <Box component="td" sx={{ p: 0, textAlign: 'center' }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      <Checkbox size="small" checked={picked.has(trip.id)}
                        onChange={() => toggle(trip.id)}
                        inputProps={{ 'aria-label': trip.trip_no }} />
                    </Box>
                    <Box component="td" className="num"
                      sx={{ px: 1.5, py: 1, fontSize: 12.5, color: c.inkMuted }}>
                      {trip.trip_no}
                    </Box>
                    <Box component="td" sx={{ px: 1.5, fontSize: 13 }}>
                      {trip.trip_date}
                    </Box>
                    <Box component="td" className="num"
                      sx={{ px: 1.5, fontSize: 13 }}>
                      {trip.vehicle_plate}
                      {trip.vehicle_kind === 'SUBCONTRACTED' && (
                        <Chip size="small" label={t('trip.hired')}
                          sx={{ ml: 1, height: 16, fontSize: 9,
                                bgcolor: c.warningSoft, color: c.warningInk }} />
                      )}
                    </Box>
                    <Box component="td" sx={{ px: 1.5, fontSize: 13 }}>
                      {trip.from_place} → {trip.to_place}
                    </Box>
                    <Box component="td" className="num"
                      sx={{ px: 1.5, fontSize: 12.5, color: c.inkMuted }}>
                      {trip.do_number ?? '—'}
                    </Box>
                    <Box component="td" className="num"
                      sx={{ px: 1.5, textAlign: 'end' }}>
                      {fmtMoney(trip.rate)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Totals from the server's preview: the figure shown is the
                figure that will be posted. */}
            <Stack direction="row" spacing={3} alignItems="center"
              sx={{ px: 2, py: 2, borderTop: `1px solid ${c.border}`,
                    flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" sx={{ color: c.inkMuted }}>
                {t('invoice.selected', { count: picked.size })}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {preview.data && (
                <Stack direction="row" spacing={3}>
                  <Figure label={t('invoice.subtotal')}
                    value={preview.data.subtotal} />
                  <Figure label={t('invoice.vat')}
                    value={preview.data.vat_amount} />
                  <Figure label={t('invoice.total')}
                    value={preview.data.total} strong />
                </Stack>
              )}
              <Button variant="contained"
                disabled={!mayInvoice || picked.size === 0 || create.isPending}
                onClick={() => create.mutate()}>
                {create.isPending ? t('common.loading') : t('invoice.create')}
              </Button>
            </Stack>
            {!mayInvoice && (
              <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                {t('invoice.noPermission')}
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Invoices already raised */}
      <Paper variant="outlined">
        <SectionHead label={t('invoice.raised')}
          right={<Typography sx={{ fontSize: 12, color: c.inkMuted }}>
            {t('invoice.shownCount', { count: shown.length })}</Typography>} />
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap"
          alignItems="center" sx={{ px: 2, py: 1.5 }}>
          <TextField size="small" label={t('invoice.search')} value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="INV/2026/00010" sx={{ width: 260 }} />
          <TextField label={t('report.from')} type="date" size="small"
            value={listFrom} onChange={(e) => setListFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
            inputProps={{ 'aria-label': t('invoice.listFrom') }} />
          <TextField label={t('report.to')} type="date" size="small"
            value={listTo} onChange={(e) => setListTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
            inputProps={{ 'aria-label': t('invoice.listTo') }} />
          {filtered && (
            <Button size="small" color="inherit"
              onClick={() => { setSearch(''); setListFrom(''); setListTo('') }}>
              {t('invoice.clearFilter')}</Button>
          )}
          {customer && (
            <Typography sx={{ fontSize: 12.5, color: c.inkMuted }}>
              {t('invoice.forCustomer')}</Typography>
          )}
        </Stack>
        {shown.length === 0 ? (
          <Typography variant="body2" sx={{ px: 2, py: 3, color: c.inkMuted }}>
            {filtered || customer ? t('invoice.noneMatch') : t('invoice.noneYet')}
          </Typography>
        ) : (
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="tbody">
              {shown.map((inv) => (
                <Fragment key={inv.id}>
                <Box component="tr"
                  sx={{ borderTop: `1px solid ${c.divider}` }}>
                  <Box component="td" className="num"
                    sx={{ px: 2, py: 1.2, fontSize: 13 }}>
                    {inv.invoice_no}
                  </Box>
                  <Box component="td" sx={{ px: 1, fontSize: 13 }}>
                    {inv.invoice_date}
                  </Box>
                  <Box component="td" sx={{ px: 1, fontSize: 13 }}>
                    {inv.customer_name}
                  </Box>
                  <Box component="td" sx={{ px: 1, fontSize: 12.5,
                                            color: c.inkMuted }}>
                    {t('invoice.tripCount', { count: inv.trip_count })}
                  </Box>
                  <Box component="td" className="num"
                    sx={{ px: 1, textAlign: 'end' }}>
                    {fmtMoney(inv.total)}
                  </Box>
                  <Box component="td" sx={{ px: 1, textAlign: 'end' }}>
                    {inv.status === 'CANCELLED' ? (
                      <Chip size="small" label={t('invoice.cancelled')}
                        sx={{ height: 19, fontSize: 10,
                              bgcolor: c.dangerSoft, color: c.dangerInk }} />
                    ) : (
                      <Stack direction="row" spacing={0.5}
                        justifyContent="flex-end">
                        {caps.data?.pdf !== false && (
                          <Button size="small" startIcon={<PictureAsPdfIcon />}
                            onClick={() => void downloadTripInvoicePdf(inv)}>
                            PDF
                          </Button>
                        )}
                        {can(P.transportCorrect) && (
                          <Button size="small"
                            onClick={() => setCorrecting(inv)}>
                            {t('correct.action')}
                          </Button>
                        )}
                        {/* An invoice with notes is corrected, not
                            cancelled: the server refuses it, so the
                            button is not offered. */}
                        {can(P.transportInvoice) && can(P.reverse)
                          && !(inv.notes ?? []).length && (
                          <Button size="small" sx={{ color: c.dangerInk }}
                            onClick={() => setCancelling(inv)}>
                            {t('invoice.cancel')}
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Box>
                {(inv.notes ?? []).length > 0 && (
                  <Box component="tr">
                    <Box component="td" colSpan={6}
                      sx={{ px: 2, pb: 1, pl: 6 }}>
                      {(inv.notes ?? []).map((n) => <NoteRow key={n.id} note={n} />)}
                    </Box>
                  </Box>
                )}
                </Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Raised */}
      <Dialog open={!!done} onClose={() => setDone(null)}>
        <DialogTitle>{t('invoice.createdTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {t('invoice.createdBody', {
              no: done?.invoice_no ?? '',
              total: done ? fmtMoney(done.total) : '' })}
          </Typography>
          <Typography variant="body2" sx={{ color: c.inkMuted }}>
            {t('invoice.createdNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          {done && caps.data?.pdf !== false && (
            <Button onClick={() => void downloadTripInvoicePdf(done)}>
              {t('invoice.downloadPdf')}
            </Button>
          )}
          <Button variant="contained" onClick={() => setDone(null)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {correcting && (
        <CorrectInvoiceDialog invoice={correcting}
          onClose={() => setCorrecting(null)} />
      )}

      <CreatePartyDialog open={newCustomer} defaultKind="customer"
        onClose={() => setNewCustomer(false)}
        onCreated={(led) => setCustomer(led.code)} />

      {/* Cancel -- reversal, never deletion */}
      <Dialog open={!!cancelling} onClose={() => setCancelling(null)}
        fullWidth maxWidth="xs">
        <DialogTitle>{t('invoice.cancelTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: c.inkMuted }}>
            {t('invoice.cancelExplain')}
          </Typography>
          {/* Said before the button, because cancelling to fix a detail
              is the mistake this prevents: the customer keeps a tax
              invoice that the books no longer have. */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('invoice.cancelUseCorrect', {
              no: cancelling?.invoice_no ?? '',
              count: cancelling?.trip_count ?? 0 })}
          </Alert>
          <TextField autoFocus fullWidth size="small" value={reason}
            onChange={(e) => setReason(e.target.value)}
            label={t('invoice.cancelReason')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelling(null)}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="error"
            disabled={!reason.trim() || cancel.isPending}
            onClick={() => cancel.mutate()}>
            {t('invoice.cancelConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

function Figure({ label, value, strong }:
{ label: string; value: string; strong?: boolean }) {
  return (
    <Box sx={{ textAlign: 'end' }}>
      <div className="label">{label}</div>
      <span className={`num ${strong ? 'num-lg num-dr' : ''}`}>
        {fmtMoney(value)}
      </span>
    </Box>
  )
}
